/*
 * Alaska Cold site script. Loaded with defer from layout/theme.liquid.
 *
 * Quote request form (snippets/ac-quote-dialog.liquid):
 * - Any element with data-quote-open opens the dialog. Optional data-quote-service,
 *   data-quote-industry and data-quote-source pre-fill the form.
 * - Links to #quote or ?quote=1 on the current page open it too, and so does landing
 *   on a URL with either.
 * - After Shopify reloads the page with a successful post, the dialog reopens in its
 *   thank you state.
 *
 * - window.AlaskaCold.openQuote(detail) opens it with answers from the estimator
 *   (assets/ac-estimator.js): hidden estimate fields, a summary above the form and a message.
 *
 * Header (sections/ac-header.liquid): desktop menu dropdowns and the mobile menu drawer.
 *
 * Tracking: events are pushed to window.dataLayer, published with Shopify.analytics.publish
 * so a Customer Events custom pixel can read them, and sent to Google Analytics 4 through
 * window.AlaskaColdAnalytics (snippets/ac-analytics.liquid) once the visitor allows analytics.
 * Event names and fields are listed in docs/quote-form.md.
 */
(function () {
  'use strict';

  /* Lets CSS tell whether the script is running (for example, to stop opening dropdowns on focus alone). */
  document.documentElement.classList.add('ac-js');

  const MIN_FILL_TIME = 3000;
  const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  const STORAGE_UTM = 'ac-utm';
  const STORAGE_SENT = 'ac-quote-sent';
  const STORAGE_ESTIMATOR = 'ac-estimator';
  const STORAGE_UNANSWERED = 'ac-ask-unmatched';
  const ESTIMATOR_FIELDS = ['estimate_range', 'estimate_inputs', 'fault_summary', 'callback_slot', 'source_module', 'question_text'];

  /* Session storage can throw in private windows or when blocked, so every call is guarded. */
  const session = {
    get(key) {
      try {
        return window.sessionStorage.getItem(key);
      } catch (error) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.sessionStorage.setItem(key, value);
        return true;
      } catch (error) {
        return false;
      }
    },
    remove(key) {
      try {
        window.sessionStorage.removeItem(key);
      } catch (error) {
        /* nothing to clear */
      }
    },
    readJson(key) {
      try {
        return JSON.parse(this.get(key) || 'null');
      } catch (error) {
        return null;
      }
    },
    isAvailable() {
      if (!this.set('ac-check', '1')) return false;
      this.remove('ac-check');
      return true;
    },
  };

  function track(eventName, data) {
    const details = data || {};
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: eventName }, details));

    try {
      const analytics = window.Shopify && window.Shopify.analytics;
      if (analytics && typeof analytics.publish === 'function') analytics.publish(eventName, details);
      /* Google Analytics 4 (snippets/ac-analytics.liquid), once the visitor allows analytics */
      if (window.AlaskaColdAnalytics) window.AlaskaColdAnalytics.event(eventName, details);
    } catch (error) {
      /* tracking must never stop the page working */
    }
  }

  /* Page scroll is held while any of our dialogs is open. Each dialog holds and releases it separately,
     so closing the menu drawer to open the quote form does not release the quote form's hold. */
  const scrollHolders = new Set();

  function holdPageScroll(holder, hold) {
    if (hold) scrollHolders.add(holder);
    else scrollHolders.delete(holder);
    document.documentElement.classList.toggle('ac-scroll-lock', scrollHolders.size > 0);
  }

  /* A press that starts and ends on a modal dialog's backdrop closes it. The dialog's panel fills the
     dialog box, so the dialog element itself is only the target when the backdrop is pressed. */
  function closeOnBackdrop(dialog, close) {
    let pressedBackdrop = false;
    dialog.addEventListener('pointerdown', (event) => {
      pressedBackdrop = event.target === dialog;
    });
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog && pressedBackdrop) close();
      pressedBackdrop = false;
    });
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* First-touch UTM parameters for this visit, written into the quote form on submit. */
  function storeUtmParameters() {
    const params = new URLSearchParams(window.location.search);
    const found = {};

    UTM_KEYS.forEach((key) => {
      const value = params.get(key);
      if (value) found[key] = value.slice(0, 150);
    });

    if (Object.keys(found).length > 0 && !session.get(STORAGE_UTM)) {
      session.set(STORAGE_UTM, JSON.stringify(found));
    }
  }

  /* Call and WhatsApp click tracking, site-wide. */
  function trackContactLinks() {
    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;

      const link = event.target.closest('a[href]');
      if (!link) return;

      const href = link.getAttribute('href') || '';
      const placed = link.closest('[data-ac-location]');
      const section = link.closest('.shopify-section');
      const clickLocation = placed ? placed.dataset.acLocation : section ? section.id : '';

      if (href.startsWith('tel:')) {
        track('call_click', { click_location: clickLocation });
      } else if (/(^|\/\/)(wa\.me|api\.whatsapp\.com|web\.whatsapp\.com)\//i.test(href)) {
        track('whatsapp_click', { click_location: clickLocation });
      }
    });
  }

  /* Matching for data-quote-service and data-quote-industry against the select options. */
  function normalise(text) {
    return String(text || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .split(' ')
      .map((word) => (word.length > 3 && word.endsWith('s') ? word.slice(0, -1) : word))
      .join(' ');
  }

  function termsFor(option) {
    const extra = option.dataset.acMatch ? option.dataset.acMatch.split('|') : [];
    return [option.value].concat(extra).map(normalise).filter(Boolean);
  }

  function findOption(options, wantedText) {
    const wanted = normalise(wantedText);
    if (!wanted) return null;

    const list = Array.from(options).filter((option) => option.value);
    const exact = list.find((option) => termsFor(option).includes(wanted));
    if (exact) return exact;

    const containing = list.find((option) => termsFor(option).some((term) => ` ${term} `.includes(` ${wanted} `)));
    if (containing) return containing;

    /* Longer text such as a page title ("Freezer rooms and blast freezers"): use the option whose
       term appears in it, preferring the longest term so the most specific option wins. */
    let best = null;
    let bestLength = 0;
    list.forEach((option) => {
      termsFor(option).forEach((term) => {
        if (term.length > bestLength && ` ${wanted} `.includes(` ${term} `)) {
          best = option;
          bestLength = term.length;
        }
      });
    });
    return best;
  }

  /* The phone box shows +44, so a UK number typed as 07... is sent as +44 7... */
  function withCountryCode(number) {
    const value = number.trim();
    if (!value || value.startsWith('+')) return value;
    if (value.startsWith('00')) return `+${value.slice(2)}`;
    return `+44 ${value.replace(/^0/, '')}`;
  }

  function initQuoteForm(root) {
    const form = root.querySelector('form');
    if (!form) return null;

    const isDialog = root instanceof HTMLDialogElement;
    const body = root.querySelector('[data-ac-quote-body]');
    const thanks = root.querySelector('[data-ac-quote-thanks]');
    const alert = root.querySelector('[data-ac-quote-alert]');
    const status = root.querySelector('[data-ac-quote-status]');
    const submitButton = root.querySelector('[data-ac-quote-submit]');
    const honeypot = form.querySelector('input[name="ac_website"]');
    const serviceSelect = form.querySelector('select[name="contact[Service]"]');
    const industrySelect = form.querySelector('select[name="contact[Business type]"]');
    const postcodeInput = form.querySelector('input[name="contact[Postcode]"]');
    const phoneInput = form.querySelector('input[name="contact[phone]"]');
    const messageInput = form.querySelector('textarea[name="contact[body]"]');
    const estimateBox = root.querySelector('[data-ac-quote-estimate]');
    const estimateTitle = root.querySelector('[data-ac-quote-estimate-title]');
    const estimateFirst = root.querySelector('[data-ac-quote-estimate-first]');
    const estimateMore = root.querySelector('[data-ac-quote-estimate-more]');
    const emailRow = root.querySelector('[data-ac-quote-email-row]');
    const emailEstimate = root.querySelector('[data-ac-quote-email-estimate]');
    const thanksCallback = root.querySelector('[data-ac-quote-thanks-callback]');
    const submitLabel = submitButton ? submitButton.textContent.trim() : '';

    let firstShownAt = isDialog ? 0 : Date.now();
    let returnFocusTo = null;
    let context = { quote_source: '', quote_service: '', source_module: '' };
    let estimatorMessage = '';
    let submitting = false;

    form.noValidate = true;

    function setMeta(name, value) {
      const input = form.querySelector(`[data-ac-meta="${name}"]`);
      if (input) input.value = value || '';
    }

    function announce(message) {
      if (status) status.textContent = message;
    }

    function scrollFormToTop() {
      if (isDialog && body) body.scrollTop = 0;
    }

    function focusFirstField() {
      const field = Array.from(
        form.querySelectorAll('input:not([type="hidden"]):not([type="checkbox"]):not([tabindex="-1"]), select, textarea')
      ).find((element) => !element.disabled && !element.closest('[hidden]'));
      if (field) field.focus();
    }

    /* Validation */

    function messageFor(field) {
      const validity = field.validity;
      if (validity.valueMissing) return field.dataset.acRequiredMessage || 'This field is required.';
      if (validity.typeMismatch || validity.patternMismatch) {
        return field.dataset.acFormatMessage || 'Check the format of this field.';
      }
      return field.validationMessage;
    }

    function setFieldError(field, message) {
      if (message) field.setAttribute('aria-invalid', 'true');
      else field.removeAttribute('aria-invalid');

      const error = field.dataset.acErrorId ? document.getElementById(field.dataset.acErrorId) : null;
      if (error) error.textContent = message;
    }

    function checkFields() {
      const invalid = [];

      form.querySelectorAll('input, select, textarea').forEach((field) => {
        if (!field.willValidate || field === honeypot) return;
        const valid = field.checkValidity();
        setFieldError(field, valid ? '' : messageFor(field));
        if (!valid) invalid.push(field);
      });

      return invalid;
    }

    function reportProblems(invalid) {
      announce(
        invalid.length === 1
          ? 'There is 1 problem with your request. Check the highlighted field.'
          : `There are ${invalid.length} problems with your request. Check the highlighted fields.`
      );
      invalid[0].focus();
    }

    function clearFieldError(event) {
      const field = event.target;
      if (!(field instanceof HTMLElement) || !('checkValidity' in field)) return;
      if (field.getAttribute('aria-invalid') === 'true' && field.checkValidity()) {
        setFieldError(field, '');
        if (!form.querySelector('[aria-invalid="true"]')) announce('');
      }
    }

    /* States */

    function showForm() {
      root.removeAttribute('data-ac-state');
      if (isDialog) root.setAttribute('aria-labelledby', 'ac-quote-title');
      if (body) body.hidden = false;
      if (thanks) thanks.hidden = true;
    }

    function showThanks() {
      root.setAttribute('data-ac-state', 'thanks');
      if (isDialog) root.setAttribute('aria-labelledby', 'ac-quote-thanks-title');
      if (body) body.hidden = true;
      if (thanks) thanks.hidden = false;
    }

    /* Open and close */

    function applyPrefill(data) {
      if (data.quoteService && serviceSelect) {
        const option = findOption(serviceSelect.options, data.quoteService);
        if (option) {
          serviceSelect.value = option.value;
          setFieldError(serviceSelect, '');
        }
        setMeta('prefill-service', data.quoteService);
      }

      if (data.quoteIndustry && industrySelect) {
        const option = findOption(industrySelect.options, data.quoteIndustry);
        if (option) industrySelect.value = option.value;
        setMeta('prefill-industry', data.quoteIndustry);
      }
    }

    /*
     * Answers handed over by the estimator (assets/ac-estimator.js): hidden fields, a summary above the
     * form, the postcode district and a message the visitor can edit. Opening the form any other way
     * clears them, so an old estimate is never sent with a new enquiry.
     */
    function applyEstimator(extra) {
      const values = extra
        ? {
            estimate_range: extra.estimateRange,
            estimate_inputs: extra.estimateInputs,
            fault_summary: extra.faultSummary,
            callback_slot: extra.callbackSlot,
            source_module: extra.sourceModule,
            question_text: extra.questionText,
          }
        : {};
      ESTIMATOR_FIELDS.forEach((name) => setMeta(name, values[name]));

      /* A short summary above the fields. On larger screens it takes the place of the intro and trust line,
         so the form still fits a 1280 x 800 screen without scrolling. */
      const summary = extra && extra.summary;
      if (summary) root.setAttribute('data-ac-estimate', '');
      else root.removeAttribute('data-ac-estimate');
      if (estimateBox) {
        estimateBox.hidden = !summary;
        if (summary && estimateTitle && estimateFirst && estimateMore) {
          const lines = (summary.lines || []).filter(Boolean);
          estimateTitle.textContent = summary.title || '';
          estimateFirst.textContent = lines[0] || '';
          estimateMore.textContent = lines.slice(1).join(' · ');
          estimateMore.hidden = lines.length < 2;
        }
      }

      const offerEmail = Boolean(extra && extra.sourceModule === 'estimate');
      if (emailRow) emailRow.hidden = !offerEmail;
      if (emailEstimate) {
        emailEstimate.disabled = !offerEmail;
        emailEstimate.checked = offerEmail && Boolean(extra.emailEstimate);
      }

      if (extra && extra.postcode && postcodeInput && !postcodeInput.value.trim()) postcodeInput.value = extra.postcode;

      if (messageInput) {
        const current = messageInput.value.trim();
        const untouched = current === '' || (estimatorMessage && current === estimatorMessage);
        if (extra && extra.message && untouched) {
          messageInput.value = extra.message;
          estimatorMessage = extra.message;
        } else if (!extra && estimatorMessage && current === estimatorMessage) {
          messageInput.value = '';
          estimatorMessage = '';
        }
      }
    }

    function sourceFor(trigger, fallback) {
      if (trigger && trigger.dataset && trigger.dataset.quoteSource) return trigger.dataset.quoteSource;
      const section = trigger && trigger.closest ? trigger.closest('.shopify-section') : null;
      return section ? section.id : fallback;
    }

    function open(trigger, fallbackSource, extra) {
      const data = Object.assign({}, (trigger && trigger.dataset) || {});
      if (extra && extra.service) data.quoteService = extra.service;

      if (root.getAttribute('data-ac-state') === 'thanks') showForm();
      if (alert) alert.hidden = true;
      announce('');
      applyPrefill(data);
      applyEstimator(extra || null);

      const source = extra && extra.source ? extra.source : sourceFor(trigger, fallbackSource || 'button');
      setMeta('source', source);
      context = {
        quote_source: source,
        quote_service: data.quoteService || '',
        source_module: (extra && extra.sourceModule) || '',
      };
      if (!firstShownAt) firstShownAt = Date.now();

      if (isDialog) {
        returnFocusTo = trigger instanceof HTMLElement ? trigger : document.activeElement;

        /* A quote button inside another dialog (the menu drawer): close that first, and send focus back
           to the button that opened it when the quote form closes. */
        const outer = trigger instanceof HTMLElement ? trigger.closest('dialog') : null;
        if (outer && outer !== root && outer.open) {
          returnFocusTo = document.querySelector(`[aria-controls="${outer.id}"]`) || returnFocusTo;
          outer.close();
        }

        if (!root.open) root.showModal();
        holdPageScroll(root, true);
      } else {
        /* The inline form on the quote and contact pages: close a dialog the visitor came from first. */
        const outer = trigger instanceof HTMLElement ? trigger.closest('dialog') : null;
        if (outer && outer.open) outer.close();
        root.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
      }

      track('quote_open', context);
      scrollFormToTop();
      focusFirstField();
    }

    function openThanks() {
      showThanks();
      if (isDialog) {
        if (!root.open) root.showModal();
        holdPageScroll(root, true);
      }
      if (thanks) thanks.focus();
    }

    function close() {
      if (isDialog && root.open) root.close();
    }

    if (isDialog) {
      root.addEventListener('close', () => {
        holdPageScroll(root, false);
        if (returnFocusTo instanceof HTMLElement && returnFocusTo.isConnected) returnFocusTo.focus();
        returnFocusTo = null;
      });

      closeOnBackdrop(root, close);
    }

    root.querySelectorAll('[data-ac-quote-close]').forEach((button) => button.addEventListener('click', close));

    form.addEventListener('input', clearFieldError);
    form.addEventListener('change', clearFieldError);

    /* Submit */

    function resetSubmitButton() {
      submitting = false;
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = submitLabel;
      }
      form.querySelectorAll('[data-ac-meta]').forEach((input) => {
        input.disabled = false;
      });
    }

    function blockSubmit(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    /*
     * Runs on window in the capture phase, before any other submit listener. Shopify's storefront
     * spam protection listens on document and calls form.submit() itself without checking whether
     * the event was cancelled, so an invalid submission has to be stopped before it gets there.
     */
    window.addEventListener(
      'submit',
      (event) => {
        if (event.target !== form) return;

        /* Shopify's spam protection may submit the form a second time; let that one through. */
        if (submitting) return;

        /* Field checks first, so a person who presses send too early still sees which fields need filling. */
        const invalid = checkFields();
        if (invalid.length) {
          blockSubmit(event);
          reportProblems(invalid);
          return;
        }

        /* Spam checks: a filled hidden field, or a valid form completed faster than a person could. */
        if (honeypot && honeypot.value.trim() !== '') {
          blockSubmit(event);
          return;
        }

        if (Date.now() - firstShownAt < MIN_FILL_TIME) {
          blockSubmit(event);
          announce('Please check your details, then send your request again.');
          return;
        }

        if (phoneInput) phoneInput.value = withCountryCode(phoneInput.value);

        const service = serviceSelect && serviceSelect.value ? serviceSelect.value : 'Not specified';
        const postcode = postcodeInput ? postcodeInput.value.trim() : '';
        const pageUrl = new URL(window.location.href);
        pageUrl.searchParams.delete('contact_posted');
        pageUrl.hash = '';

        const callbackInput = form.querySelector('[data-ac-meta="callback_slot"]');
        const callbackSlot = callbackInput ? callbackInput.value : '';
        setMeta(
          'subject',
          `${callbackSlot ? `Call-back at ${callbackSlot}: ` : 'Quote request: '}${service}${postcode ? ` in ${postcode}` : ''}`
        );
        setMeta('page-url', pageUrl.toString());

        const utm = session.readJson(STORAGE_UTM) || {};
        UTM_KEYS.forEach((key) => setMeta(key, utm[key]));

        /* Questions the Ask a question card could not answer during this visit (assets/ac-estimator.js). */
        const unanswered = session.readJson(STORAGE_UNANSWERED);
        setMeta('unanswered_questions', Array.isArray(unanswered) ? unanswered.join(' | ').slice(0, 1500) : '');

        /* Leave empty tracking fields out of the email. */
        form.querySelectorAll('[data-ac-meta]').forEach((input) => {
          input.disabled = input.value === '';
        });

        submitting = true;
        announce('');
        session.set(
          STORAGE_SENT,
          JSON.stringify({
            quote_source: context.quote_source,
            quote_service: service,
            source_module: context.source_module,
            callback_slot: callbackSlot,
            email_estimate: Boolean(emailEstimate && !emailEstimate.disabled && emailEstimate.checked),
          })
        );
        /* The estimator's answers and the unanswered questions have done their job once they are sent. */
        session.remove(STORAGE_ESTIMATOR);
        session.remove(STORAGE_UNANSWERED);
        track('quote_submit', { quote_source: context.quote_source, quote_service: service, source_module: context.source_module });

        if (submitButton) {
          submitButton.disabled = true;
          submitButton.textContent = 'Sending…';
        }

        /* If a spam check keeps the visitor on this page, give the button back. */
        window.setTimeout(resetSubmitButton, 10000);
      },
      true
    );

    /* Coming back with the browser's back button restores the page from cache: reset the button. */
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) resetSubmitButton();
    });

    /* Initial state */

    const url = new URL(window.location.href);
    const posted = root.dataset.acPosted === 'true';
    const hasErrors = root.dataset.acErrors === 'true';
    const sent = session.readJson(STORAGE_SENT);

    if (posted && (sent || !session.isAvailable())) {
      /* Shopify reloaded the page after a successful post from this form. */
      session.remove(STORAGE_SENT);
      if (thanksCallback && sent && (sent.callback_slot || sent.email_estimate)) {
        const lines = [];
        if (sent.callback_slot) lines.push(`We will call you at ${sent.callback_slot}.`);
        if (sent.email_estimate) lines.push('We will email your budget estimate to you.');
        thanksCallback.textContent = lines.join(' ');
        thanksCallback.hidden = false;
      }
      track('quote_success', sent ? { quote_source: sent.quote_source, quote_service: sent.quote_service, source_module: sent.source_module } : {});
      openThanks();

      url.searchParams.delete('contact_posted');
      if (url.hash === '#ac-quote-form' || url.hash === '#contact_form') url.hash = '';
      window.history.replaceState(window.history.state, '', url.toString());
    } else if (posted) {
      /* Another contact form on this page was posted, not this one. */
      showForm();
    } else if (hasErrors && sent) {
      session.remove(STORAGE_SENT);
      showForm();
      if (isDialog) {
        if (!root.open) root.showModal();
        holdPageScroll(root, true);
      }
      if (alert) {
        alert.hidden = false;
        alert.setAttribute('tabindex', '-1');
        alert.focus();
      }
    } else if (url.hash === '#quote') {
      open(null, 'link-hash');
    } else if (url.searchParams.get('quote') === '1') {
      open(null, 'link-parameter');
    }

    return { open };
  }

  /* For assets/ac-estimator.js: tracking, the shared page scroll hold and opening the quote form with answers. */
  window.AlaskaCold = Object.assign(window.AlaskaCold || {}, { track, holdPageScroll });

  /* Desktop menu dropdowns. The chevron button is a disclosure; with a mouse, hover shows the
     dropdown too (see ac-site.css). A click goes by what is visible, not by the stored state, so
     clicking the chevron while the dropdown is showing through hover closes it instead of doing
     nothing: the item holds the hover rule off until the pointer leaves. */
  function initDropdowns() {
    const items = Array.from(document.querySelectorAll('[data-ac-dropdown]'));
    if (!items.length) return;

    function setOpen(item, open) {
      item.classList.toggle('is-open', open);
      const toggle = item.querySelector('[aria-expanded]');
      if (toggle) toggle.setAttribute('aria-expanded', String(open));
    }

    items.forEach((item) => {
      const toggle = item.querySelector('[aria-expanded]');
      const dropdown = item.querySelector('.ac-nav__dropdown');
      if (!toggle || !dropdown) return;

      toggle.addEventListener('click', () => {
        const visible = getComputedStyle(dropdown).display !== 'none';
        items.forEach((other) => setOpen(other, false));
        if (visible) {
          item.classList.add('is-hover-off');
        } else {
          item.classList.remove('is-hover-off');
          setOpen(item, true);
        }
      });

      item.addEventListener('pointerleave', () => item.classList.remove('is-hover-off'));

      item.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') {
          setOpen(item, false);
          toggle.focus();
        }
      });

      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget)) setOpen(item, false);
      });
    });

    document.addEventListener('click', (event) => {
      items.forEach((item) => {
        if (!item.contains(event.target)) setOpen(item, false);
      });
    });
  }

  /* Mobile menu drawer: a native modal dialog, so focus stays inside it and Escape closes it. */
  function initDrawer() {
    const drawer = document.querySelector('[data-ac-drawer]');
    if (!(drawer instanceof HTMLDialogElement)) return;

    const openers = document.querySelectorAll('[data-ac-drawer-open]');
    const desktop = window.matchMedia('(min-width: 990px)');

    function setExpanded(open) {
      openers.forEach((button) => button.setAttribute('aria-expanded', String(open)));
    }

    function close() {
      if (drawer.open) drawer.close();
    }

    openers.forEach((button) =>
      button.addEventListener('click', () => {
        if (drawer.open) return;
        drawer.showModal();
        holdPageScroll(drawer, true);
        setExpanded(true);
      })
    );

    drawer.addEventListener('close', () => {
      holdPageScroll(drawer, false);
      setExpanded(false);
    });

    drawer.querySelectorAll('[data-ac-drawer-close]').forEach((button) => button.addEventListener('click', close));
    closeOnBackdrop(drawer, close);

    /* A link to a part of the current page should close the drawer so the page can scroll to it. */
    drawer.addEventListener('click', (event) => {
      const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (link && link.hash && link.pathname === window.location.pathname) close();
    });

    /* The drawer is for small screens only. */
    desktop.addEventListener('change', (event) => {
      if (event.matches) close();
    });
  }

  function isQuoteLink(link) {
    let url;
    try {
      url = new URL(link.href, window.location.href);
    } catch (error) {
      return false;
    }
    if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) return false;
    return url.hash === '#quote' || url.searchParams.get('quote') === '1';
  }

  /* Footer "Cookie settings" link: reopens Shopify's cookie preferences. The link's own address
     (the privacy policy's cookie section) is the fallback when the banner script is not loaded. */
  function initCookieSettings() {
    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const link = event.target.closest('[data-ac-cookie-settings]');
      if (!link) return;
      const banner = window.privacyBanner;
      if (!banner || typeof banner.showPreferences !== 'function') return;
      event.preventDefault();
      banner.showPreferences();
    });
  }

  function init() {
    storeUtmParameters();
    trackContactLinks();
    initCookieSettings();
    initDropdowns();
    initDrawer();

    const root = document.querySelector('[data-ac-quote]');
    const quote = root ? initQuoteForm(root) : null;
    if (!quote) return;

    window.AlaskaCold.openQuote = (options) => {
      const detail = options || {};
      quote.open(detail.trigger || null, detail.source || 'estimator', detail);
    };

    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;

      const trigger = event.target.closest('[data-quote-open]');
      if (trigger) {
        event.preventDefault();
        quote.open(trigger);
        return;
      }

      const link = event.target.closest('a[href]');
      if (link && !link.closest('[data-ac-quote]') && isQuoteLink(link)) {
        event.preventDefault();
        quote.open(link, 'link');
      }
    });

    window.addEventListener('hashchange', () => {
      if (window.location.hash === '#quote') quote.open(null, 'link-hash');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
